import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CompanyService } from 'src/app/company/company.service';
import { IOperatorClass } from './OperatorClass';
import { OperatorInput, OperatorOutput } from './models/models';
import { Labels } from 'src/app/shared/labels';

const FIXED_COLUMNS = 3;
const STATE_CODE_COLUMN = 2;
const UD_ROW_CODE = 'UD';
const UD_DIRECT_SHEET_INDEX = 6;
const UD_SUPPLEMENTAL_SHEET_INDEX = 7;

export class UserDefinedDataOperator implements IOperatorClass {
    private readonly companyService = inject(CompanyService);

    private getCellValue(worksheet: any, row: number, col: number): any {
        const value = worksheet.rows(row).cells(col).value;
        return value !== undefined ? value : null;
    }

    private readSheetData(worksheet: any, udColumns: any[], existingPremium: any[]): any[] {
        const data = existingPremium.map(row => ({ ...row }));
        const TOTAL_COL = udColumns.length - 1; // last col is "Total" — it's a formula cell; we compute it from the other cols

        // Collect which premium column indices have a header present in row 0.
        // Fixed columns and the Total column are excluded from this check:
        // fixed cols are always read, Total is computed (its formula cell returns null in Ignite UI).
        const columnsWithHeaders = new Set<number>();
        for (let col = FIXED_COLUMNS; col < TOTAL_COL; col++) {
            const headerValue = this.getCellValue(worksheet, 0, col);
            if (headerValue != null && String(headerValue).trim() !== '') {
                columnsWithHeaders.add(col);
            }
        }

        let rowIndex = 1;
        let dataRowIndex = 0;

        while (this.getCellValue(worksheet, rowIndex, 0) !== null) {
            let stateCode = '';

            for (let col = 0; col < udColumns.length; col++) {
                let value: any = null;

                if (col < FIXED_COLUMNS) {
                    value = this.getCellValue(worksheet, rowIndex, col);
                } else if (col === TOTAL_COL) {
                    // Total is a formula cell — compute from the UD data cols already written for this row
                    let sum = 0;
                    for (let c = FIXED_COLUMNS; c < TOTAL_COL; c++) {
                        const v = data[dataRowIndex]?.[c];
                        if (v !== null && v !== undefined) sum += Number(v);
                    }
                    value = sum;
                } else if (columnsWithHeaders.has(col)) {
                    // Only extract premium values for columns that have a header in row 0
                    const raw = this.getCellValue(worksheet, rowIndex, col);
                    if (raw !== null && raw !== '') {
                        const num = parseFloat(String(raw));
                        value = !isNaN(num) ? Math.round(num) : null;
                    }
                }
                // Premium columns without a header retain null — effectively cleared

                if (col === STATE_CODE_COLUMN) {
                    stateCode = String(value ?? '').trim();
                }

                if (stateCode === UD_ROW_CODE) {
                    break;
                }

                if (data[dataRowIndex] != null) {
                    data[dataRowIndex][col] = value;
                }
            }

            rowIndex++;
            dataRowIndex++;
        }

        return data;
    }

    async runOperator(operatorInput: OperatorInput): Promise<OperatorOutput> {
        const workbook = operatorInput.workbook;
        if (!workbook) {
            return { status: 'error', message: ['Workbook not found.'], operatorInput };
        }

        const directSheet = workbook.worksheets(UD_DIRECT_SHEET_INDEX);
        if (!directSheet) {
            return { status: 'error', message: [`${Labels.premium.udDirect} sheet not found.`], operatorInput };
        }

        const supplementalSheet = workbook.worksheets(UD_SUPPLEMENTAL_SHEET_INDEX);
        if (!supplementalSheet) {
            return { status: 'error', message: [`${Labels.premium.udSupplemental} sheet not found.`], operatorInput };
        }

        const premiumData$ = this.companyService.getPremiumReportData(
            operatorInput.prfnumber,
            operatorInput.prfyear,
            operatorInput.prfGroup,
        );
        if (!premiumData$) {
            return { status: 'error', message: ['Failed to load premium report data.'], operatorInput };
        }

        let resp: any;
        try {
            resp = await firstValueFrom(premiumData$);
        } catch {
            return { status: 'error', message: ['Failed to load premium report data.'], operatorInput };
        }

        if (resp?.message !== 'Success') {
            return { status: 'error', message: ['Failed to load premium report data.'], operatorInput };
        }

        const result = resp['data'];
        const columns: any[] = result['columns'];
        const rows: any[] = result['rows'];
        const udColumns: any[] = result['userdefined_columns'];
        const directPremium: any[] = result['data']['direct_premium'];
        const supplementalPremium: any[] = result['data']['supplemental_premium'];
        const udPremiumDirect: any[] = result['userdefined_premium']['dpremium'];
        const udPremiumSupplemental: any[] = result['userdefined_premium']['spremium'];

        if (udColumns.length === 0) {
            return { status: 'skipped', operatorInput };
        }

        const updatedDirect = this.readSheetData(directSheet, udColumns, udPremiumDirect);
        const updatedSupplemental = this.readSheetData(supplementalSheet, udColumns, udPremiumSupplemental);

        const griddata = {
            premreportlobs: columns,
            premreportstates: rows,
            dpremiuminfo: directPremium,
            spremiuminfo: supplementalPremium,
            udpremiuminfo: updatedDirect,
            uspremiuminfo: updatedSupplemental,
            udlobs: udColumns,
        };

        const save$ = this.companyService.updatepremiumdetails(
            operatorInput.prfyear,
            operatorInput.prfnumber ?? '',
            griddata,
            operatorInput.prfGroup ?? '',
            true,
        );
        if (!save$) {
            return { status: 'error', message: ['Failed to save user defined premium data.'], operatorInput };
        }

        let saveResp: any;
        try {
            saveResp = await firstValueFrom(save$);
        } catch {
            return { status: 'error', message: ['Failed to save user defined premium data.'], operatorInput };
        }

        if (saveResp?.message !== 'Success') {
            return {
                status: 'error',
                message: [saveResp?.status_msg ?? 'Failed to save user defined premium data.'],
                operatorInput,
            };
        }

        return { status: 'success', operatorInput };
    }
}
